import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Website } from '@/api/types';
import { useSession } from '@/app/session';
import { Button, ConfirmHeader, KeyValueList, Modal, ModalFooter, Segmented, useModalTitleId, useToast } from '@/design-system';
import { themeQuery } from '@/features/designer/api';
import { demoOf, domainsQuery, productionBinding } from '@/features/domains/api';
import { rulesQuery } from '@/features/settings/api';
import { errorText } from '@/shell/errorText';
import {
  activeDeployment,
  deploymentsKey,
  deploymentsQuery,
  productionBlock,
  publish,
  publishProduction,
  refreshAfterDeploy,
  runningDeployment,
  type Channel,
  type DeploymentOut,
} from './api';
import s from './deploy.module.css';

/** The revision a publish ships: the website's active variant label («B · r14»), else the approved theme's revision. */
export function usePublishRevision(site: Website): string | null {
  const theme = useQuery({ ...themeQuery(site.id), enabled: !site.variant });
  if (site.variant) return site.variant;
  return theme.data ? `r${theme.data.source_revision.number}` : null;
}

/** The demo host: the website's demo address, else the domain the website reports. */
export function useDemoHost(site: Website): string | null {
  const domains = useQuery(domainsQuery(site.id));
  return demoOf(domains.data)?.hostname ?? site.domain ?? null;
}

const uuid = () => (typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`);

/**
 * «Опубликовать B · r14» (DESIGN_SPEC §4.12): channel «Demo · noindex», the demo domain, the revision and
 * «Rollback атомарный, v24»; «Опубликовать в демо» starts the publish job (P5-B01) with an idempotency key
 * made when the modal opens, so a double click publishes once.
 *
 * P6-D02 (additive, C10): once the website is past the handoff (or has a verified production domain) a channel
 * choice «Demo · noindex» / «Production» sits above the rows. Production names why it is not available (the
 * scope, the stage, the domain) instead of hiding; when it is, the rows describe the switch and the button
 * leads to a re-confirmation: the PM types the production domain before «Переключить production».
 */
export function PublishModal({ site, open, onClose }: { site: Website; open: boolean; onClose: () => void }) {
  const { t } = useTranslation('deploy');
  const { t: tc } = useTranslation('common');
  const { can, isAdmin } = useSession();
  const titleId = useModalTitleId();
  const qc = useQueryClient();
  const toast = useToast();
  const deployments = useQuery(deploymentsQuery(site.id));
  const domains = useQuery(domainsQuery(site.id));
  const rules = useQuery({ ...rulesQuery, enabled: isAdmin && open });
  const rev = usePublishRevision(site);
  const host = useDemoHost(site);
  // One key per opening: a double click (or a retried request) publishes once.
  const key = useRef(uuid());
  const [busy, setBusy] = useState(false);
  const live = site.status === 'production';
  const [channel, setChannel] = useState<Channel>(live ? 'production' : 'demo');
  const [confirming, setConfirming] = useState(false);
  const [typed, setTyped] = useState('');
  const prod = productionBinding(domains.data);
  const block = productionBlock({ allowed: can('deployment:publish_production'), status: site.status, domainStatus: prod?.status ?? null });
  // The choice appears once production is in sight: past the handoff, or with a verified production domain.
  const offerProduction = site.status === 'pending_production' || live || prod?.status === 'verified' || prod?.status === 'active';
  const production = channel === 'production' && !block;
  const close = () => {
    key.current = uuid();
    setConfirming(false);
    setTyped('');
    setChannel(live ? 'production' : 'demo');
    onClose();
  };
  const dash = tc('dash');
  const active = activeDeployment(deployments.data, production ? 'production' : 'demo');
  const policy = rules.data?.values.demo_after_production;

  const pickChannel = (c: Channel) => {
    if (c === 'production' && block) {
      toast(t(`publish.block.${block}`));
      return;
    }
    setChannel(c);
    setConfirming(false);
    setTyped('');
  };

  const start = () => {
    if (busy) return;
    const running = runningDeployment(deployments.data);
    if (running) {
      toast(t('publish.running', { version: running.version }));
      close();
      return;
    }
    if (production && !confirming) {
      setConfirming(true);
      return;
    }
    if (production && (!prod || typed.trim().toLowerCase() !== prod.hostname)) {
      toast(t('publish.confirm_mismatch', { domain: prod?.hostname ?? dash }));
      return;
    }
    setBusy(true);
    (production && prod ? publishProduction(site.id, key.current, prod.hostname) : publish(site.id, key.current))
      .then((r) => {
        qc.setQueryData<DeploymentOut[]>(deploymentsKey(site.id), (list) => [r.deployment, ...(list ?? []).filter((d) => d.id !== r.deployment.id)]);
        toast(production ? t('publish.started_production', { version: r.deployment.version, domain: prod?.hostname ?? dash }) : t('publish.started', { version: r.deployment.version }));
        refreshAfterDeploy(qc, site.id);
        close();
      })
      .catch((e: unknown) => toast(errorText(e, tc)))
      .finally(() => setBusy(false));
  };

  const rows = production
    ? [
        { key: 'channel', label: t('publish.channel'), value: t('publish.channel_production') },
        { key: 'domain', label: t('publish.domain'), value: prod?.hostname ?? dash },
        { key: 'revision', label: t('publish.revision'), value: rev ?? dash },
        { key: 'demo', label: t('publish.demo_after'), value: policy ? t(`publish.demo_policy.${policy}`) : t('publish.demo_policy.server') },
        { key: 'rollback', label: t('publish.rollback'), value: active ? t('publish.rollback_value', { version: active.version }) : t('publish.rollback_first') },
      ]
    : [
        { key: 'channel', label: t('publish.channel'), value: t('publish.channel_demo') },
        { key: 'domain', label: t('publish.domain'), value: host ?? dash },
        { key: 'revision', label: t('publish.revision'), value: rev ?? dash },
        { key: 'rollback', label: t('publish.rollback'), value: active ? t('publish.rollback_value', { version: active.version }) : t('publish.rollback_first') },
      ];

  return (
    <Modal open={open} onClose={close} labelledBy={titleId}>
      <ConfirmHeader
        id={titleId}
        title={confirming ? t('publish.confirm_title') : t('publish.title', { rev: rev ?? dash })}
        text={confirming ? t('publish.confirm_text', { domain: prod?.hostname ?? dash, demo: host ?? dash }) : production ? t('publish.text_production') : t('publish.text')}
      />
      {offerProduction && !confirming && (
        <div className={s.channel}>
          <Segmented
            variant="form"
            label={t('publish.channel')}
            value={production ? 'production' : 'demo'}
            onChange={pickChannel}
            options={[
              { value: 'demo', label: t('publish.channel_demo') },
              { value: 'production', label: t('publish.channel_production_short') },
            ]}
          />
          {block && <span className={s.channelHint}>{t(`publish.block.${block}`)}</span>}
        </div>
      )}
      {confirming ? (
        <label className={s.confirmField}>
          <span className={s.confirmLabel}>{t('publish.confirm_label', { domain: prod?.hostname ?? dash })}</span>
          <input
            className={s.confirmInput}
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder={prod?.hostname ?? ''}
            autoComplete="off"
            spellCheck={false}
            autoFocus
          />
        </label>
      ) : (
        <KeyValueList rows={rows} />
      )}
      <ModalFooter>
        <Button variant="secondary" size={46} onClick={confirming ? () => setConfirming(false) : close}>
          {confirming ? t('publish.back') : tc('actions.cancel')}
        </Button>
        <Button size={46} aria-busy={busy} aria-disabled={(confirming && typed.trim().toLowerCase() !== prod?.hostname) || undefined} onClick={start}>
          {confirming ? t('publish.confirm_submit') : production ? t('publish.submit_production') : t('publish.submit')}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

/**
 * Follows the website's publications from any tab: when an activating deployment becomes active or fails,
 * it toasts and refreshes the website (its status moves the lifecycle bar), the domains and the history.
 */
export function usePublishWatch(site: Website): void {
  const { t } = useTranslation('deploy');
  const { t: tc } = useTranslation('common');
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery(deploymentsQuery(site.id));
  const watching = useRef(new Set<string>());
  useEffect(() => {
    for (const d of q.data ?? []) {
      if (d.status === 'activating') {
        watching.current.add(d.id);
        continue;
      }
      if (!watching.current.has(d.id)) continue;
      watching.current.delete(d.id);
      if (d.status === 'active') toast(d.channel === 'production' ? t('publish.done_production', { version: d.version }) : t('publish.done', { version: d.version }));
      else if (d.status === 'failed') {
        const code = d.error_code ?? 'publish_failed';
        toast(t('publish.failed', { version: d.version, reason: (tc as unknown as (k: string, o: Record<string, unknown>) => string)(`errors.${code}`, { defaultValue: code }) }));
      }
      refreshAfterDeploy(qc, site.id);
    }
  }, [q.data, qc, site.id, t, tc, toast]);
}
